import { getTimeToRead } from "@edgetier/utilities";
import { IRole, Route } from "@edgetier/watchtower-types";
import Axios, { AxiosError } from "axios";
import { axios } from "utilities/axios";
import toast from "react-hot-toast";

import { useMutation, UseMutationOptions, useQueryClient } from "react-query";
import { getServerError } from "utilities/get-server-error";

interface IProps {
    readonly role?: IRole;
}

/**
 * Create a request with POST for a new role or update the role with PUT
 * @param role  Optional existing role
 */
const createOrUpdateRole = (role: IRole | undefined) => async (submittedRole: string) => {
    const trimRoleValues = submittedRole.trim();

    // Check if a note was passed which will mean a PATCH should be made instead of a POST.
    if (typeof role !== "undefined") {
        await axios.patch(Route.Role.replace(":roleId", role.roleId), { role: trimRoleValues });
        return { ...role, role: trimRoleValues };
    } else {
        const { data } = await axios.post<IRole>(Route.Roles, { role: trimRoleValues });
        return { ...data };
    }
};

/**
 * Custom hook to create or update an existing role and updating the roles query cache
 * @param props.role    Optional existing role
 * @returns A mutation to create or update an existing role
 */
export const useCreateRole = ({ role }: IProps, configuration: UseMutationOptions<IRole, AxiosError, string> = {}) => {
    const queryClient = useQueryClient();

    /**
     * Handles creating or editing a role and update the roles query cache to include the
     * @param updatedRole   Newly modified role
     */
    const onSuccess = (updatedRole: IRole) => {
        // Update the roles query
        queryClient.setQueryData<IRole[]>([Route.Roles], (roles): IRole[] => {
            if (typeof role === "undefined") {
                return [...(roles ?? []), updatedRole];
            } else {
                return (roles ?? []).map((existingRole) =>
                    existingRole.roleId === updatedRole.roleId ? updatedRole : existingRole
                );
            }
        });

        const successMessage =
            typeof role === "undefined"
                ? `Successfully created ${updatedRole.role}`
                : `Successfully updated ${updatedRole.role}`;

        toast.success(successMessage, { duration: getTimeToRead(successMessage, 1000) });
    };
    /**
     * Show a toast message with the request error
     */
    const onError = (error: unknown) => {
        if (Axios.isAxiosError(error)) {
            toast.error(getServerError(error));
        } else {
            if (typeof role !== "undefined") {
                toast.error("Failed to edit a role");
            } else {
                toast.error("Failed to create a role");
            }
        }
    };

    const mutationSettings = { onSuccess, onError, ...configuration };
    return useMutation<IRole, AxiosError, string>(createOrUpdateRole(role), mutationSettings);
};
